import React from 'react';
import { BAND_FILL, BAND_TEXT, BAND_LABEL } from './riskBands';

/**
 * The exposure × impact matrix, populated.
 *
 * The same grid, the same five bands and the same colours as the one in every
 * delivered report — deliberately. Anyone who has read a Mindcore report can
 * read this without being taught it, and a dashboard that scored risk its own
 * way would be a second opinion about the same engagements.
 *
 * Cells carry a count, not a heat gradient. The band already carries severity;
 * shading the cell by how many findings landed in it would encode two things in
 * one channel and make a single critical finding look milder than four low ones.
 *
 * Laid out as a CSS grid with five equal `1fr` columns and a fixed aspect on
 * each cell. A table sizes its columns to their content, which on real data
 * meant a cell holding "15" was wider than one holding "6" and the matrix came
 * out visibly deformed — the one thing a risk matrix cannot afford, since its
 * whole claim is that position means something.
 *
 * Cells are slightly wider than tall, as the report's are (`.risk-matrix-table`
 * pads 6px against 8px). Square cells make the grid read as a chequerboard;
 * a little width makes it read as a table of levels, which is what it is.
 */

// Width : height of a single cell. Every cell shares it, so the grid stays
// regular — the deformation this replaced was cells disagreeing, not the shape.
const CELL_ASPECT = '6 / 5';

const AXIS = ['Very low', 'Low', 'Medium', 'High', 'Very high'];

function RiskMatrix({ cells = [], onSelect, selected }) {
  if (!cells.length) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        No findings carry a risk estimate yet.
      </div>
    );
  }

  // The API returns cells highest-impact first, which is the reading order.
  const rows = [];
  for (let i = 0; i < cells.length; i += 5) rows.push(cells.slice(i, i + 5));

  const columns = '3.5rem repeat(5, minmax(0, 1fr))';

  return (
    <div className="flex">
      <div className="flex items-center pr-2">
        <span
          className="text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Impact
        </span>
      </div>

      <div className="flex-1 min-w-0 max-w-[30rem]">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: columns }}>
          {rows.map((row) => (
            <React.Fragment key={row[0].impact}>
              <div className="flex items-center justify-end pr-1 text-[10px] text-slate-500 text-right leading-tight">
                {AXIS[row[0].impact - 1]}
              </div>
              {row.map((cell) => {
                const empty = cell.count === 0;
                const active = selected
                  && selected.exposure === cell.exposure
                  && selected.impact === cell.impact;
                return (
                  <button
                    key={`${cell.exposure}-${cell.impact}`}
                    type="button"
                    disabled={empty}
                    onClick={() => onSelect && onSelect(cell)}
                    title={`Exposure ${cell.exposure} · Impact ${cell.impact} — ${
                      BAND_LABEL[cell.band] || cell.band} · ${cell.count} finding${
                      cell.count === 1 ? '' : 's'}`}
                    className={`w-full rounded-md flex items-center justify-center
                      text-sm font-semibold tabular-nums transition
                      ${empty ? 'cursor-default' : 'cursor-pointer hover:ring-2 hover:ring-white/50'}
                      ${active ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}
                    style={{
                      aspectRatio: CELL_ASPECT,
                      backgroundColor: BAND_FILL[cell.band] || '#94a3b8',
                      color: BAND_TEXT[cell.band] || 'white',
                      // Empty cells keep their band colour but recede, so the
                      // shape of the matrix stays legible while the eye goes to
                      // where findings actually are.
                      opacity: empty ? 0.16 : 1,
                    }}
                  >
                    {empty ? '' : cell.count}
                  </button>
                );
              })}
            </React.Fragment>
          ))}

          <div />
          {AXIS.map((label) => (
            <div key={label} className="text-[10px] text-slate-500 text-center pt-1 leading-tight">
              {label}
            </div>
          ))}
        </div>

        <div className="text-[10px] uppercase tracking-widest text-slate-500 text-center mt-2">
          Exposure factor
        </div>
      </div>
    </div>
  );
}

export default RiskMatrix;
